import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "dt_admin_token";

// sessionStorage clears on tab close (safer than localStorage for auth tokens).
// A stronger design would use httpOnly cookies; that requires backend changes.
export const getToken = () => sessionStorage.getItem(TOKEN_KEY);
export const setToken = (t) => sessionStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => sessionStorage.removeItem(TOKEN_KEY);
const authHeader = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const fmtDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("en-AU", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

/* ---------- LOGIN ---------- */
export function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    if (getToken()) navigate("/admin");
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      setToken(data.access_token);
      toast.success("Welcome back!");
      navigate("/admin");
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="admin-login-shell">
      <div className="admin-login-card">
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
          <div style={{width:36,height:36,background:"var(--gold)",clipPath:"polygon(50% 0%,100% 100%,0% 100%)"}}></div>
          <div>
            <span style={{fontFamily:"var(--fd)",fontSize:18,fontWeight:800,color:"var(--navy)",letterSpacing:".06em",textTransform:"uppercase",display:"block"}}>Delta Talent</span>
            <span style={{fontFamily:"var(--fd)",fontSize:10,fontWeight:600,letterSpacing:".18em",textTransform:"uppercase",color:"var(--gold-dark)"}}>Admin Portal</span>
          </div>
        </div>
        <h2>Sign In</h2>
        <p style={{color:"var(--text-light)"}}>Access your leads, messages, and CVs.</p>
        <form onSubmit={submit} data-testid="admin-login-form">
          {err && <div className="form-err">{err}</div>}
          <div className="fg"><label>Email</label>
            <input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="admin-email" placeholder="admin@deltatalent.com.au"/>
          </div>
          <div className="fg"><label>Password</label>
            <input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="admin-password" placeholder="••••••••"/>
          </div>
          <button type="submit" className="btn btn-navy" style={{width:"100%",justifyContent:"center",marginTop:8, opacity: loading ? .7 : 1}} disabled={loading} data-testid="admin-login-submit">
            {loading ? "Signing in..." : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ---------- DATA HOOK ---------- */
function useAdminData(onUnauthorized) {
  const [stats, setStats] = useState({ talent_requests: 0, messages: 0, cv_submissions: 0 });
  const [talent, setTalent] = useState([]);
  const [messages, setMessages] = useState([]);
  const [cvs, setCvs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, t, m, c] = await Promise.all([
          axios.get(`${API}/admin/stats`, authHeader()),
          axios.get(`${API}/admin/talent-requests`, authHeader()),
          axios.get(`${API}/admin/messages`, authHeader()),
          axios.get(`${API}/admin/cv-submissions`, authHeader()),
        ]);
        if (cancelled) return;
        setStats(s.data); setTalent(t.data); setMessages(m.data); setCvs(c.data);
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 401) onUnauthorized();
        else toast.error("Failed to load data");
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [onUnauthorized]);

  return { stats, talent, messages, cvs, loading };
}

/* ---------- TABLES ---------- */
const TalentTable = ({ rows }) => (
  <table className="admin-table">
    <thead><tr><th>Date</th><th>Company</th><th>Contact</th><th>Trade</th><th>Workers</th><th>Start</th><th>Duration</th><th>Details</th></tr></thead>
    <tbody>
      {rows.map(r => (
        <tr key={r.id}>
          <td>{fmtDate(r.created_at)}</td>
          <td><strong>{r.company_name}</strong></td>
          <td>{r.contact_person}<br/><span style={{color:"var(--text-light)",fontSize:13}}>{r.email}<br/>{r.phone}</span></td>
          <td>{r.trade_type}</td>
          <td>{r.num_workers}</td>
          <td>{r.start_date || "-"}</td>
          <td>{r.duration || "-"}</td>
          <td style={{maxWidth:280}}>{r.project_details || "-"}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const MessagesTable = ({ rows }) => (
  <table className="admin-table">
    <thead><tr><th>Date</th><th>Name</th><th>Contact</th><th>Role</th><th>Message</th></tr></thead>
    <tbody>
      {rows.map(r => (
        <tr key={r.id}>
          <td>{fmtDate(r.created_at)}</td>
          <td><strong>{r.name}</strong></td>
          <td>{r.email}<br/><span style={{color:"var(--text-light)",fontSize:13}}>{r.phone || "-"}</span></td>
          <td>{r.role || "-"}</td>
          <td style={{maxWidth:400,whiteSpace:"pre-wrap"}}>{r.message}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const CvsTable = ({ rows, onDownload }) => (
  <table className="admin-table">
    <thead><tr><th>Date</th><th>Name</th><th>Contact</th><th>Trade</th><th>Experience</th><th>Availability</th><th>Licences</th><th>CV</th></tr></thead>
    <tbody>
      {rows.map(r => (
        <tr key={r.id}>
          <td>{fmtDate(r.created_at)}</td>
          <td><strong>{r.full_name}</strong></td>
          <td>{r.email}<br/><span style={{color:"var(--text-light)",fontSize:13}}>{r.phone}</span></td>
          <td>{r.trade}</td>
          <td>{r.experience || "-"}</td>
          <td>{r.availability || "-"}</td>
          <td style={{maxWidth:220}}>{r.licences || "-"}</td>
          <td>
            {r.storage_path ? (
              <button className="admin-btn admin-btn-gold" onClick={() => onDownload(r.id, r.original_filename)} data-testid={`cv-download-${r.id}`}>Download</button>
            ) : <span style={{color:"var(--text-light)",fontSize:13}}>No file</span>}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

/* ---------- DASHBOARD ---------- */
export function AdminDashboard() {
  const [tab, setTab] = useState("talent");
  const navigate = useNavigate();
  const hasToken = !!getToken();

  const handleUnauthorized = useCallback(() => {
    clearToken();
    navigate("/admin/login");
  }, [navigate]);

  const { stats, talent, messages, cvs, loading } = useAdminData(handleUnauthorized);

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  const downloadCV = async (id, filename) => {
    try {
      const res = await axios.get(`${API}/admin/cv/${id}/download`, { ...authHeader(), responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || `cv-${id}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download CV");
    }
  };

  if (!hasToken) return <Navigate to="/admin/login" replace />;

  const renderTab = () => {
    if (tab === "talent") return talent.length === 0 ? <div className="admin-empty">No talent requests yet.</div> : <TalentTable rows={talent} />;
    if (tab === "messages") return messages.length === 0 ? <div className="admin-empty">No messages yet.</div> : <MessagesTable rows={messages} />;
    if (tab === "cvs") return cvs.length === 0 ? <div className="admin-empty">No CV submissions yet.</div> : <CvsTable rows={cvs} onDownload={downloadCV} />;
    return null;
  };

  return (
    <div className="admin-wrap" data-testid="admin-dashboard">
      <header className="admin-header">
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{width:32,height:32,background:"var(--gold)",clipPath:"polygon(50% 0%,100% 100%,0% 100%)"}}></div>
          <div>
            <span className="logo-text">Delta Talent</span>
            <span style={{fontFamily:"var(--fd)",fontSize:10,fontWeight:600,letterSpacing:".18em",textTransform:"uppercase",color:"var(--gold)",display:"block",marginTop:-3}}>Admin Portal</span>
          </div>
        </div>
        <button className="admin-logout" onClick={logout} data-testid="admin-logout">Sign Out</button>
      </header>

      <div className="admin-container">
        <div className="admin-stats">
          <div className="admin-stat"><div className="admin-stat-label">Talent Requests</div><div className="admin-stat-value" data-testid="stat-talent">{stats.talent_requests}</div></div>
          <div className="admin-stat"><div className="admin-stat-label">Messages</div><div className="admin-stat-value" data-testid="stat-messages">{stats.messages}</div></div>
          <div className="admin-stat"><div className="admin-stat-label">CV Submissions</div><div className="admin-stat-value" data-testid="stat-cvs">{stats.cv_submissions}</div></div>
        </div>

        <div className="admin-nav">
          <button className={`admin-tab ${tab==="talent"?"active":""}`} onClick={() => setTab("talent")} data-testid="tab-talent">Talent Requests</button>
          <button className={`admin-tab ${tab==="messages"?"active":""}`} onClick={() => setTab("messages")} data-testid="tab-messages">Messages</button>
          <button className={`admin-tab ${tab==="cvs"?"active":""}`} onClick={() => setTab("cvs")} data-testid="tab-cvs">CV Submissions</button>
        </div>

        <div className="admin-card">
          {loading ? <div className="admin-empty">Loading...</div> : (
            <div className="admin-table-wrap">{renderTab()}</div>
          )}
        </div>
      </div>
    </div>
  );
}
